import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from './baseQuery';
import { apiConstants } from '../common/constants';
import {
  hydrateAllocationFromApi,
  setActiveMerchantFromApi,
  setApprovalToken,
} from './d2cAllocationSlice';

const BASE = 'allocation';

export const allocationApiSlice = createApi({
  reducerPath: 'allocationApi',
  baseQuery,
  tagTypes: ['AllocationMerchants', 'AllocationDetail', 'AllocationRules', 'AllocationChangelog', 'SupplyCosts'],
  endpoints: (builder) => ({

    // ── Page 1: Merchant Listing ─────────────────────────────────────────────
    getAllocationMerchants: builder.query({
      queryFn: async ({ q, tier, status, page = 0, limit = 20 } = {}, _api, _extra, bq) => {
        try {
          const params = new URLSearchParams({ page, limit });
          if (q)      params.set('q', q);
          if (tier)   params.set('tier', tier);
          if (status) params.set('status', status);
          const res = await bq({ url: `${BASE}/merchants?${params}`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      providesTags: ['AllocationMerchants'],
    }),

    // ── Page 2: Merchant Detail ──────────────────────────────────────────────
    getAllocationMerchantDetail: builder.query({
      queryFn: async (merchantId, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${BASE}/merchants/${merchantId}`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (merchantId, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const merchant = data?.data ?? data;
          if (merchant) dispatch(setActiveMerchantFromApi(merchant));
        } catch {}
      },
      providesTags: (_, __, id) => [{ type: 'AllocationDetail', id }],
    }),

    patchAllocationMerchant: builder.mutation({
      queryFn: async ({ merchantId, ...body }, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${BASE}/merchants/${merchantId}`, data: body, type: 'PATCH' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      invalidatesTags: (_, __, { merchantId }) => [{ type: 'AllocationDetail', id: merchantId }],
    }),

    // ── Page 3: Allocation Rules (load draft or published) ───────────────────
    getAllocationRules: builder.query({
      queryFn: async (merchantId, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${BASE}/merchants/${merchantId}/allocation`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          if (err?.response?.status === 404) return { data: { source: 'none' } };
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const rules = data?.data ?? data;
          if (rules && rules.source !== 'none') dispatch(hydrateAllocationFromApi(rules));
        } catch {}
      },
      providesTags: (_, __, id) => [{ type: 'AllocationRules', id }],
    }),

    getAllocationCsv: builder.query({
      queryFn: async (merchantId, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${BASE}/merchants/${merchantId}/allocation/csv`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    saveAllocationDraft: builder.mutation({
      queryFn: async ({ merchantId, ...body }, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${BASE}/merchants/${merchantId}/allocation`, data: body, type: 'POST' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      invalidatesTags: (_, __, { merchantId }) => [{ type: 'AllocationRules', id: merchantId }],
    }),

    // ── Page 4: Preview Stats ────────────────────────────────────────────────
    getAllocationPreviewStats: builder.query({
      queryFn: async (merchantId, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${BASE}/merchants/${merchantId}/preview-stats`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    // ── OTP Flow ─────────────────────────────────────────────────────────────
    requestAllocationOtp: builder.mutation({
      queryFn: async ({ merchantId }, _api, _extra, bq) => {
        try {
          const res = await bq({
            url: `${BASE}/otp/request`,
            data: { merchantId, purpose: 'ALLOCATION_PUBLISH' },
            type: 'POST',
          });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    verifyAllocationOtp: builder.mutation({
      queryFn: async ({ otp_id, merchantId, code }, _api, _extra, bq) => {
        try {
          const res = await bq({
            url: `${BASE}/otp/verify`,
            data: { otp_id, merchantId, code, purpose: 'ALLOCATION_PUBLISH' },
            type: 'POST',
          });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const result = data?.data ?? data;
          if (result?.verified && result?.approvalToken) {
            dispatch(setApprovalToken(result.approvalToken));
          }
        } catch {}
      },
    }),

    // ── Publish ──────────────────────────────────────────────────────────────
    publishAllocation: builder.mutation({
      queryFn: async ({ merchantId, approvalToken, ...body }, _api, _extra, bq) => {
        try {
          const headers = approvalToken ? { 'X-Approval-Token': approvalToken } : {};
          const res = await bq({
            url: `${BASE}/publish/${merchantId}`,
            data: body,
            type: 'POST',
            headers,
          });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      invalidatesTags: ['AllocationMerchants', 'AllocationChangelog'],
    }),

    // ── Changelog ────────────────────────────────────────────────────────────
    getAllocationChangelog: builder.query({
      queryFn: async ({ merchantId, page = 0, limit = 20 }, _api, _extra, bq) => {
        try {
          const res = await bq({
            url: `${BASE}/changelog/${merchantId}?page=${page}&limit=${limit}`,
            type: 'GET',
          });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      providesTags: ['AllocationChangelog'],
    }),

    getVersionCsv: builder.query({
      queryFn: async ({ merchantId, version }, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${BASE}/csv/${merchantId}/${version}`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    // ── Margin Preview (Highest Prozo Margin mode) ────────────────────────────
    // Backend-authoritative lane-wise margins (merchant rate − supply cost). Returns
    // two views from one call:
    //   • acSummary    — per-AC breakdown sorted by avgMargin desc (AC selection cards)
    //   • lanePriority — per-lane ranked courier list sorted zone→weight→payment
    //                    (the lane drill-down table; rank = publish assignment order)
    // Legacy flat-array responses are coerced into the acSummary view.
    getMarginPreview: builder.query({
      queryFn: async (merchantId, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${BASE}/margin-preview/${merchantId}`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      transformResponse: (res) => {
        const d = res?.data ?? res ?? {};
        if (Array.isArray(d)) return { acSummary: d, lanePriority: [] };
        return {
          acSummary: Array.isArray(d.acSummary) ? d.acSummary : [],
          lanePriority: Array.isArray(d.lanePriority) ? d.lanePriority : [],
        };
      },
    }),

    // ── AC Meta ──────────────────────────────────────────────────────────────
    getAcMeta: builder.query({
      queryFn: async (_arg, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${BASE}/ac/meta`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      transformResponse: (res) => {
        // API now returns { [ac]: AcMetaDto } keyed by account code.
        // Backward compat: also handle legacy array response.
        const data = res?.data ?? res;
        const normalizeDir = (dir) => {
          const u = String(dir || '').toUpperCase();
          if (u === 'REVERSE' || u === 'RVP') return 'Reverse';
          return 'Forward';
        };
        if (Array.isArray(data)) {
          const map = {};
          data.forEach((item) => {
            if (item.ac) map[item.ac] = { ...item, direction: normalizeDir(item.direction) };
          });
          return map;
        }
        if (data && typeof data === 'object') {
          const map = {};
          Object.entries(data).forEach(([k, v]) => {
            map[k] = { ...v, direction: normalizeDir(v?.direction) };
          });
          return map;
        }
        return {};
      },
    }),

    // ── Supply Costs ─────────────────────────────────────────────────────────
    getSupplyCosts: builder.query({
      queryFn: async (_arg, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${BASE}/supply-costs`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      transformResponse: (res) => Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []),
      providesTags: ['SupplyCosts'],
    }),

    // ── Supply Costs Upload ───────────────────────────────────────────────────
    uploadSupplyCosts: builder.mutation({
      queryFn: async ({ file }, _api, _extra, bq) => {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const res = await bq({ url: `${BASE}/supply-costs/upload`, data: formData, type: 'POST' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      invalidatesTags: ['AllocationMerchants', 'SupplyCosts'],
    }),

    // ── Sample CSV Download ───────────────────────────────────────────────────
    downloadSupplyCostSample: builder.query({
      queryFn: async (_arg, _api, _extra, bq) => {
        try {
          const res = await bq({
            url: 'order/Download_samples?fileName=D2C_SUPPLY_COST_UPLOAD',
            type: 'GET',
          });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    // ── Revenue Grid ─────────────────────────────────────────────────────────
    getRevenueGrid: builder.query({
      queryFn: async (merchantId, _api, _extra, bq) => {
        try {
          const res = await bq({ url: `${BASE}/revenue/${merchantId}`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      transformResponse: (res) => Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []),
    }),

  }),
});

export const {
  useGetAllocationMerchantsQuery,
  useGetAllocationMerchantDetailQuery,
  useLazyGetAllocationMerchantDetailQuery,
  usePatchAllocationMerchantMutation,
  useGetAllocationRulesQuery,
  useLazyGetAllocationRulesQuery,
  useLazyGetAllocationCsvQuery,
  useSaveAllocationDraftMutation,
  useGetAllocationPreviewStatsQuery,
  useRequestAllocationOtpMutation,
  useVerifyAllocationOtpMutation,
  usePublishAllocationMutation,
  useGetAllocationChangelogQuery,
  useLazyGetVersionCsvQuery,
  useGetAcMetaQuery,
  useGetMarginPreviewQuery,
  useGetSupplyCostsQuery,
  useUploadSupplyCostsMutation,
  useLazyDownloadSupplyCostSampleQuery,
  useGetRevenueGridQuery,
} = allocationApiSlice;
